<template>
  <div class="console-layout">
    <ConsoleSideMenu :collapsed="sideCollapsed" @toggle-collapse="sideCollapsed = !sideCollapsed" />
    <div class="console-main">
      <ConsoleHeader />
      <div class="console-content">
        <div class="console-router-view">
          <keep-alive>
            <router-view />
          </keep-alive>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConsoleHeader from '@/components/console/ConsoleHeader.vue'
import ConsoleSideMenu from '@/components/console/ConsoleSideMenu.vue'

export default {
  name: 'ConsoleLayout',
  components: { ConsoleHeader, ConsoleSideMenu },
  data() {
    return {
      sideCollapsed: false,
      // 当前选中的企业上下文 — 子页面通过 inject 获取
      currentDeptId: this.$route.query.deptId || null,
      currentDeptName: null,
      currentSiteId: this.$route.query.siteId || null,
      currentSiteName: null,
    }
  },
  provide() {
    return {
      // ponytail: 传函数而非对象，子组件 inject 后调用 this.orgContext() 获取最新值
      orgContext: () => ({
        deptId: this.currentDeptId,
        deptName: this.currentDeptName,
        siteId: this.currentSiteId,
        siteName: this.currentSiteName,
        selectDept: this.selectDept,
        selectSite: this.selectSite,
      }),
    }
  },
  methods: {
    selectDept(deptId, deptName) {
      this.currentDeptId = deptId
      this.currentDeptName = deptName
      // 同步到 query — 刷新后保留
      this.$router.replace({
        query: { ...this.$route.query, deptId },
      })
    },
    selectSite(siteId, siteName) {
      this.currentSiteId = siteId
      this.currentSiteName = siteName
      this.$router.replace({
        query: { ...this.$route.query, siteId },
      })
    },
  },
  watch: {
    '$route.query.deptId': {
      immediate: true,
      handler(val) {
        if (val) this.currentDeptId = val
      },
    },
    '$route.query.siteId': {
      immediate: true,
      handler(val) {
        if (val) this.currentSiteId = val
      },
    },
  },
}
</script>

<style scoped>
.console-layout {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}
.console-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.console-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ecf0f6;
  min-height: 0;
}
.console-router-view {
  flex: 1;
  overflow: auto;
  padding: 12px;
}
/* ponytail: 子页面自身带 padding 时（如 station-index），去掉继承的 padding */
.console-router-view:has(> .main-center) {
  padding: 0;
}
/* ponytail: 所有页面根元素从路由层继承高度 */
.console-router-view > * {
  min-height: 100%;
}
</style>

<style lang="scss">
@import '@/components/console/console-theme.scss';

// ========================================
// 控制台全局 Element-UI 样式覆盖
// 仅影响控制台下的组件，不影响看板
// ========================================
.console-layout {
  // 按钮
  .el-button--primary {
    @include console-btn-primary;
    padding: 8px 16px;
    font-size: 13px;
  }
  .el-button--default {
    @include console-btn-default;
    padding: 8px 16px;
    font-size: 13px;
  }
  .el-button--text {
    @include console-btn-text;
    font-size: 13px;
  }
  .el-button--small {
    padding: 5px 12px;
    font-size: 12px;
    height: 28px;
  }
  .el-button--mini {
    padding: 4px 8px;
    font-size: 12px;
    height: 24px;
  }

  // 输入框
  .el-input {
    @include console-input;
  }
  .el-select {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }
  .el-date-editor {
    .el-input__inner {
      height: 32px;
      line-height: 32px;
    }
  }

  // 表格
  @include console-table;

  // 分页
  @include console-pagination;

  // 弹窗
  @include console-dialog;

  // 标签页 — 用于控制台内的子标签页
  .console-tab-container .el-tabs--card {
    @include console-tabs;
  }

  // 选择器
  .el-select-dropdown__item {
    &.selected {
      color: $console-primary;
      font-weight: 600;
    }
    &:hover {
      background: $console-primary-bg;
    }
  }

  // 开关
  .el-switch.is-checked .el-switch__core {
    border-color: $console-primary;
    background-color: $console-primary;
  }

  // 单选框
  .el-radio__input.is-checked .el-radio__inner {
    border-color: $console-primary;
    background: $console-primary;
  }
  .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    background-color: $console-primary;
    border-color: $console-primary;
    box-shadow: -1px 0 0 0 $console-primary;
  }

  // 多选框
  .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: $console-primary;
    border-color: $console-primary;
  }
  .el-checkbox-button__orig-radio:checked + .el-checkbox-button__inner {
    background-color: $console-primary;
    border-color: $console-primary;
    box-shadow: -1px 0 0 0 $console-primary;
  }

  // 级联选择器
  .el-cascader-node.in-active-path,
  .el-cascader-node.is-selectable.in-checked-path,
  .el-cascader-node.is-active {
    color: $console-primary;
  }

  // 加载
  .el-loading-spinner .path {
    stroke: $console-primary;
  }
  .el-loading-text {
    color: $console-primary;
  }

  // ========================================
  // 控制台详情页 — 全局排版美化
  // 仅保留真正通用的规则，页面特化样式放在各自页面
  // ========================================

  .console-router-view > * {
    // --- 操作按钮行（告警页面、配置页面等）---
    .table__list__btn {
      color: $console-primary;
      cursor: pointer;
      margin: 0 4px;
      font-size: 13px;
      transition: $console-transition;
      &:hover {
        color: $console-primary-hover;
        text-decoration: underline;
      }
    }

    // --- 站点详情页标签页 ---
    .stationDetailPage .detail-tabs-bar {
      display: flex;
      gap: 0;
      margin-bottom: 0;
      background: $console-bg-white;
      border-radius: 0;
      overflow: hidden;
      box-shadow: none;

      .tab-btn {
        padding: 12px 24px;
        font-size: 14px;
        font-weight: 500;
        color: $console-text-secondary;
        cursor: pointer;
        border-bottom: 2px solid transparent;
        transition: $console-transition;
        &:hover {
          color: $console-primary;
        }
        &.active {
          color: $console-primary;
          border-bottom-color: $console-primary;
          font-weight: 600;
        }
      }
    }

    // --- stationListPage 左侧组织树 & 右侧列表 ---
    .stationListPage .main-page .left-tree,
    .stationListPage .main-page .right-list {
      box-shadow: $console-shadow-sm;
      border-radius: $console-radius-md;
      overflow: hidden;
    }
  }
}
</style>
